/**
 * Route configuration for breadcrumbs.
 *
 * Each route pattern maps to breadcrumb metadata including:
 * - label: The display text for the breadcrumb segment
 * - resolver: Optional entity type to resolve dynamic names (e.g., "team" -> team.name)
 *
 * Patterns use :param syntax to match dynamic segments.
 */
var _a;
export var EntityResolver;
(function (EntityResolver) {
    EntityResolver["Team"] = "team";
    EntityResolver["League"] = "league";
    EntityResolver["Venue"] = "venue";
    EntityResolver["Player"] = "player";
    EntityResolver["Season"] = "season";
    EntityResolver["Division"] = "division";
    EntityResolver["IntraSquadMatch"] = "intraSquadMatch";
    EntityResolver["ExternalMatch"] = "externalMatch";
    EntityResolver["IntraSquadTournament"] = "intraSquadTournament";
    EntityResolver["IntraSquadTournamentMatch"] = "intraSquadTournamentMatch";
    EntityResolver["Transfer"] = "transfer";
    EntityResolver["LeagueMatch"] = "leagueMatch";
})(EntityResolver || (EntityResolver = {}));
/**
 * Route path constants for use across the application.
 * Single source of truth for all route paths.
 */
export var ROUTES = {
    // Home
    HOME: "/",
    LOGIN: "/login",
    // Teams
    TEAMS: "/teams",
    TEAMS_CREATE: "/teams/create",
    TEAM_DETAIL: "/teams/:id",
    TEAM_EDIT: "/teams/:id/edit",
    TEAM_STATS: "/teams/:id/stats",
    TEAM_VENUES: "/teams/:id/venues",
    TEAM_VENUES_ADD: "/teams/:id/venues/add",
    TEAM_PREFERENCES_EDIT: "/teams/:id/preferences/edit",
    TEAM_ADD_PLAYER: "/teams/:id/add-player",
    TEAM_ADD_PLAYER_CREATE: "/teams/:id/add-player/create",
    TEAM_ADD_PLAYER_EXISTING: "/teams/:id/add-player/existing",
    TEAM_FORMATION: "/teams/:id/formation",
    TEAM_ASSISTANT_MANAGERS: "/teams/:id/assistant-managers",
    TEAM_SQUAD: "/teams/:id/squad",
    TEAM_SQUAD_EDIT: "/teams/:id/squad/:playerId/edit",
    TEAM_SQUAD_MERGE: "/teams/:id/squad/merge",
    TEAM_INTRA_SQUAD_MATCH_CREATE: "/teams/:id/intra-squad-match/create",
    TEAM_EXTERNAL_MATCH_CREATE: "/teams/:id/external-match/create",
    TEAM_INTRA_SQUAD_TOURNAMENTS_CREATE: "/teams/:id/intra-squad-tournaments/create",
    TEAM_INVITATIONS: "/teams/:id/invitations",
    TEAM_INVITATIONS_SEND: "/teams/:id/invitations/send",
    // Invitations
    MY_INVITATIONS: "/invitations",
    INVITATION_DETAIL: "/invitations/:id",
    JOIN_TEAM_BY_LINK: "/join/team/:token",
    JOIN_TEAM_BY_INVITATION: "/join/invitation/:token",
    POST_JOIN_PROFILE: "/welcome/profile/:teamId",
    // League-Team Invitations
    LEAGUE_TEAM_INVITATION_DETAIL: "/league-invitations/:id",
    // Team Ownership Invitations
    MY_OWNERSHIP_INVITATIONS: "/ownership-invitations",
    OWNERSHIP_INVITATION_DETAIL: "/ownership-invitations/:id",
    TEAM_OWNERSHIP_INVITATIONS: "/teams/:id/ownership-invitations",
    TEAM_OWNERSHIP_INVITATIONS_SEND: "/teams/:id/ownership-invitations/send",
    ACCEPT_OWNERSHIP_BY_TOKEN: "/accept/ownership/:token",
    // Venues
    VENUES: "/venues",
    VENUES_CREATE: "/venues/create",
    VENUE_DETAIL: "/venues/:id",
    VENUE_PITCH_CREATE: "/venues/:venueId/pitches/create",
    // Players
    PLAYER_DETAIL: "/players/:id",
    PLAYER_EDIT: "/players/:id/edit",
    // Leagues
    LEAGUES: "/leagues",
    LEAGUES_CREATE: "/leagues/create",
    LEAGUE_DETAIL: "/leagues/:id",
    LEAGUE_EDIT: "/leagues/:id/edit",
    LEAGUE_SEASONS_CREATE: "/leagues/:id/seasons/create",
    LEAGUE_VENUES: "/leagues/:id/venues",
    LEAGUE_VENUES_ADD: "/leagues/:id/venues/add",
    LEAGUE_TEAMS_ADD: "/leagues/:id/teams/add",
    LEAGUE_TEAMS_ADD_EXISTING: "/leagues/:id/teams/add-existing",
    LEAGUE_TEAMS_CREATE: "/leagues/:id/teams/create",
    LEAGUE_TEAMS_MANAGE: "/leagues/:id/teams/manage",
    LEAGUE_TEAMS: "/leagues/:id/teams",
    LEAGUE_INVITATIONS: "/leagues/:id/invitations",
    LEAGUE_INVITATIONS_SEND: "/leagues/:id/invitations/send",
    // Seasons
    SEASON_DETAIL: "/leagues/:leagueId/seasons/:id",
    SEASON_EDIT: "/leagues/:leagueId/seasons/:id/edit",
    SEASON_DIVISIONS_CREATE: "/leagues/:leagueId/seasons/:seasonId/divisions/create",
    // Divisions
    DIVISION_EDIT: "/leagues/:leagueId/seasons/:seasonId/divisions/:id/edit",
    // Matches (nested under teams for breadcrumbs)
    INTRA_SQUAD_MATCH_DETAIL: "/teams/:teamId/intra-squad-matches/:id",
    INTRA_SQUAD_MATCH_UPDATE: "/teams/:teamId/intra-squad-matches/:id/update",
    INTRA_SQUAD_MATCH_STATS_UPDATE: "/teams/:teamId/intra-squad-matches/:id/stats/update",
    EXTERNAL_MATCH_DETAIL: "/teams/:teamId/external-matches/:id",
    EXTERNAL_MATCH_UPDATE: "/teams/:teamId/external-matches/:id/update",
    EXTERNAL_MATCH_STATS_UPDATE: "/teams/:teamId/external-matches/:id/stats/update",
    INTRA_SQUAD_TOURNAMENT_MATCH_STATS_UPDATE: "/teams/:teamId/intra-squad-tournament-matches/:id/stats/update",
    LEAGUE_MATCH_CREATE: "/leagues/:leagueId/seasons/:seasonId/matches/create",
    LEAGUE_MATCH_DETAIL: "/leagues/:leagueId/seasons/:seasonId/matches/:id",
    LEAGUE_MATCH_UPDATE: "/leagues/:leagueId/seasons/:seasonId/matches/:id/update",
    LEAGUE_MATCH_STATS_UPDATE: "/leagues/:leagueId/seasons/:seasonId/matches/:id/stats/update",
    // Tournaments (nested under teams for breadcrumbs)
    INTRA_SQUAD_TOURNAMENT_DETAIL: "/teams/:teamId/intra-squad-tournaments/:id",
    // Transfers
    TRANSFER_DETAIL: "/transfers/:id",
    // Search
    SEARCH: "/search",
    SEARCH_PLAYERS: "/search/players",
    SEARCH_TEAMS: "/search/teams",
    // Account & Settings
    ACCOUNT: "/account",
    PRIVACY_SETTINGS: "/privacy-settings",
    NOTIFICATIONS: "/notifications",
    // External Users
    EXTERNAL_USER_UPDATE: "/external-users/:id/update",
    // Public pages
    ABOUT_US: "/about-us",
    PRIVACY_POLICY: "/privacy-policy",
    TERMS_OF_SERVICE: "/terms-of-service",
    ADDING_PLAYERS: "/adding-players",
};
/**
 * Maps route patterns to their breadcrumb configuration.
 * TypeScript enforces that every route in ROUTES has a config entry.
 */
export var routeConfig = (_a = {},
    // Home
    _a[ROUTES.HOME] = { label: "Home" },
    _a[ROUTES.LOGIN] = { label: "Login" },
    // Teams
    _a[ROUTES.TEAMS] = { label: "Teams" },
    _a[ROUTES.TEAMS_CREATE] = { label: "Create Team" },
    _a[ROUTES.TEAM_DETAIL] = { label: "Team", resolver: EntityResolver.Team },
    _a[ROUTES.TEAM_EDIT] = { label: "Edit" },
    _a[ROUTES.TEAM_STATS] = { label: "Stats" },
    _a[ROUTES.TEAM_VENUES] = { label: "Venues" },
    _a[ROUTES.TEAM_VENUES_ADD] = { label: "Add Venue" },
    _a[ROUTES.TEAM_PREFERENCES_EDIT] = { label: "Edit Preferences" },
    _a[ROUTES.TEAM_ADD_PLAYER] = { label: "Add Player" },
    _a[ROUTES.TEAM_ADD_PLAYER_CREATE] = { label: "Create Player" },
    _a[ROUTES.TEAM_ADD_PLAYER_EXISTING] = { label: "Add Existing Players" },
    _a[ROUTES.TEAM_FORMATION] = { label: "Formation" },
    _a[ROUTES.TEAM_ASSISTANT_MANAGERS] = { label: "Assistant Managers" },
    _a[ROUTES.TEAM_SQUAD] = { label: "Squad" },
    _a[ROUTES.TEAM_SQUAD_EDIT] = { label: "Edit Membership" },
    _a[ROUTES.TEAM_SQUAD_MERGE] = { label: "Merge Players" },
    _a[ROUTES.TEAM_INTRA_SQUAD_MATCH_CREATE] = { label: "Create Intra-Squad Match" },
    _a[ROUTES.TEAM_EXTERNAL_MATCH_CREATE] = { label: "Create External Match" },
    _a[ROUTES.TEAM_INTRA_SQUAD_TOURNAMENTS_CREATE] = { label: "Create Tournament" },
    _a[ROUTES.TEAM_INVITATIONS] = { label: "Invitations" },
    _a[ROUTES.TEAM_INVITATIONS_SEND] = { label: "Send Invitation" },
    // Venues
    _a[ROUTES.VENUES] = { label: "Venues" },
    _a[ROUTES.VENUES_CREATE] = { label: "Create Venue" },
    _a[ROUTES.VENUE_DETAIL] = { label: "Venue", resolver: EntityResolver.Venue },
    _a[ROUTES.VENUE_PITCH_CREATE] = { label: "Add Pitch" },
    // Players
    _a[ROUTES.PLAYER_DETAIL] = { label: "Player", resolver: EntityResolver.Player },
    _a[ROUTES.PLAYER_EDIT] = { label: "Edit" },
    // External Users
    _a[ROUTES.EXTERNAL_USER_UPDATE] = { label: "Update External User" },
    // Matches
    _a[ROUTES.INTRA_SQUAD_MATCH_DETAIL] = {
        label: "Intra-Squad Match",
        resolver: EntityResolver.IntraSquadMatch,
    },
    _a[ROUTES.INTRA_SQUAD_MATCH_UPDATE] = { label: "Update" },
    _a[ROUTES.INTRA_SQUAD_MATCH_STATS_UPDATE] = { label: "Update Stats" },
    _a[ROUTES.EXTERNAL_MATCH_DETAIL] = {
        label: "External Match",
        resolver: EntityResolver.ExternalMatch,
    },
    _a[ROUTES.EXTERNAL_MATCH_UPDATE] = { label: "Update" },
    _a[ROUTES.EXTERNAL_MATCH_STATS_UPDATE] = { label: "Update Stats" },
    // Tournaments
    _a[ROUTES.INTRA_SQUAD_TOURNAMENT_DETAIL] = {
        label: "Tournament",
        resolver: EntityResolver.IntraSquadTournament,
    },
    _a[ROUTES.INTRA_SQUAD_TOURNAMENT_MATCH_STATS_UPDATE] = {
        label: "Update Tournament Match Stats",
    },
    _a[ROUTES.LEAGUE_MATCH_CREATE] = {
        label: "Create Match",
    },
    _a[ROUTES.LEAGUE_MATCH_DETAIL] = {
        label: "League Match",
        resolver: EntityResolver.LeagueMatch,
    },
    _a[ROUTES.LEAGUE_MATCH_UPDATE] = {
        label: "Update",
    },
    _a[ROUTES.LEAGUE_MATCH_STATS_UPDATE] = {
        label: "Update Stats",
    },
    // Leagues
    _a[ROUTES.LEAGUES] = { label: "Leagues" },
    _a[ROUTES.LEAGUES_CREATE] = { label: "Create League" },
    _a[ROUTES.LEAGUE_DETAIL] = { label: "League", resolver: EntityResolver.League },
    _a[ROUTES.LEAGUE_EDIT] = { label: "Edit" },
    _a[ROUTES.LEAGUE_VENUES] = { label: "Venues" },
    _a[ROUTES.LEAGUE_VENUES_ADD] = { label: "Add Venue" },
    _a[ROUTES.LEAGUE_TEAMS_ADD] = { label: "Add Team" },
    _a[ROUTES.LEAGUE_TEAMS_ADD_EXISTING] = { label: "Add Existing Teams" },
    _a[ROUTES.LEAGUE_TEAMS_CREATE] = { label: "Create Team" },
    _a[ROUTES.LEAGUE_TEAMS_MANAGE] = { label: "Manage Teams" },
    _a[ROUTES.LEAGUE_TEAMS] = { label: "Teams" },
    _a[ROUTES.LEAGUE_INVITATIONS] = { label: "Invitations" },
    _a[ROUTES.LEAGUE_INVITATIONS_SEND] = { label: "Invite Team" },
    _a[ROUTES.LEAGUE_SEASONS_CREATE] = { label: "Create Season" },
    // Seasons
    _a[ROUTES.SEASON_DETAIL] = { label: "Season", resolver: EntityResolver.Season },
    _a[ROUTES.SEASON_EDIT] = { label: "Edit" },
    _a[ROUTES.SEASON_DIVISIONS_CREATE] = { label: "Create Division" },
    // Divisions
    _a[ROUTES.DIVISION_EDIT] = {
        label: "Edit Division",
        resolver: EntityResolver.Division,
    },
    // Transfers
    _a[ROUTES.TRANSFER_DETAIL] = {
        label: "Transfer Request",
        resolver: EntityResolver.Transfer,
    },
    // Invitations
    _a[ROUTES.MY_INVITATIONS] = { label: "Invitations" },
    _a[ROUTES.INVITATION_DETAIL] = { label: "Invitation" },
    _a[ROUTES.JOIN_TEAM_BY_LINK] = { label: "Join Team" },
    _a[ROUTES.JOIN_TEAM_BY_INVITATION] = { label: "Accept Invitation" },
    _a[ROUTES.POST_JOIN_PROFILE] = { label: "Set Up Profile" },
    // League-Team Invitations
    _a[ROUTES.LEAGUE_TEAM_INVITATION_DETAIL] = { label: "League Invitation" },
    // Team Ownership Invitations
    _a[ROUTES.MY_OWNERSHIP_INVITATIONS] = { label: "Ownership Invitations" },
    _a[ROUTES.OWNERSHIP_INVITATION_DETAIL] = { label: "Ownership Invitation" },
    _a[ROUTES.TEAM_OWNERSHIP_INVITATIONS] = { label: "Ownership Invitations" },
    _a[ROUTES.TEAM_OWNERSHIP_INVITATIONS_SEND] = { label: "Transfer Ownership" },
    _a[ROUTES.ACCEPT_OWNERSHIP_BY_TOKEN] = { label: "Accept Ownership" },
    // User Account
    _a[ROUTES.ACCOUNT] = { label: "Account" },
    _a[ROUTES.PRIVACY_SETTINGS] = { label: "Privacy Settings" },
    _a[ROUTES.NOTIFICATIONS] = { label: "Notifications" },
    // Search
    _a[ROUTES.SEARCH] = { label: "Search" },
    _a[ROUTES.SEARCH_PLAYERS] = { label: "Player Results" },
    _a[ROUTES.SEARCH_TEAMS] = { label: "Team Results" },
    // Static Pages
    _a[ROUTES.ABOUT_US] = { label: "About Us" },
    _a[ROUTES.PRIVACY_POLICY] = { label: "Privacy Policy" },
    _a[ROUTES.TERMS_OF_SERVICE] = { label: "Terms of Service" },
    _a[ROUTES.ADDING_PLAYERS] = { label: "Adding Players" },
    _a);
/**
 * Extracts all route patterns from the config.
 * Used for testing to ensure all App.tsx routes are covered.
 */
export var getAllRoutePatterns = function () { return Object.keys(routeConfig); };
